import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Crown, Calendar } from "lucide-react";

const PERK_LABELS = {
    deep_cleans: "Deep clean",
    regular_cleans: "Regular clean",
    ironing: "Ironing pickup",
    wash_fold: "Wash & fold",
    express: "Express slot",
    glam: "Glam service",
};

const TIER_NAMES = {
    vip_gold: "Gold Household",
    vip_diamond: "Diamond Household",
    vip_platinum: "Platinum Corporate",
};

function daysUntil(iso) {
    if (!iso) return null;
    const d = new Date(iso).getTime() - Date.now();
    return Math.max(0, Math.ceil(d / (1000 * 60 * 60 * 24)));
}

export default function VIPStatusCard() {
    const [state, setState] = useState({ loading: true, data: null });

    useEffect(() => {
        api.get("/vip/me/subscription")
            .then(({ data }) => setState({ loading: false, data }))
            .catch(() => setState({ loading: false, data: null }));
    }, []);

    if (state.loading) return null;
    const d = state.data;
    if (!d) return null;

    // Non-member soft upsell
    if (!d.active) {
        return (
            <div className="rounded-sm border border-amber-800/30 bg-gradient-to-br from-amber-950/20 to-transparent p-6" data-testid="vip-upsell-card">
                <div className="flex items-center gap-3 mb-2">
                    <Crown size={18} strokeWidth={1.5} className="gold-text" />
                    <div className="overline">Membership</div>
                </div>
                <div className="font-serif text-2xl tracking-tight">Save more every month.</div>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                    Gold from $130.99/mo — 1 monthly deep clean, weekly ironing, and zero booking fees on Diamond+.
                </p>
                <Link
                    to="/vip"
                    className="mt-4 inline-block text-xs uppercase tracking-[0.2em] gold-text border-b border-amber-700/40 pb-1"
                    data-testid="vip-upsell-cta"
                >
                    See membership tiers →
                </Link>
            </div>
        );
    }

    const remaining = d.remaining || {};
    const usage = d.usage || {};
    const perkRows = Object.entries(remaining).filter(
        ([k]) => (d.entitlements || {})[k] > 0
    );
    const daysLeft = daysUntil(d.renews_at);

    return (
        <div
            className="rounded-sm border border-amber-700/40 bg-gradient-to-br from-amber-950/25 via-transparent to-transparent p-6 lg:p-8"
            data-testid="vip-status-card"
        >
            <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                    <div className="flex items-center gap-2 mb-2">
                        <Crown size={16} strokeWidth={1.5} className="gold-text" />
                        <div className="overline">Active membership</div>
                    </div>
                    <div className="font-serif text-3xl tracking-tight" data-testid="vip-tier-name">
                        {TIER_NAMES[d.tier] || "SMART VIP"}
                    </div>
                </div>
                <div className="text-right">
                    <div className="overline">Renews in</div>
                    <div className="font-serif text-2xl mt-1 flex items-center gap-2 justify-end" data-testid="vip-renews-in">
                        <Calendar size={16} strokeWidth={1.5} className="gold-text" />
                        {daysLeft ?? "—"}
                        <span className="text-xs text-muted-foreground uppercase tracking-[0.2em]">days</span>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="vip-ledger-grid">
                {perkRows.map(([k, rem]) => {
                    const total = (d.entitlements || {})[k] || 0;
                    const used = usage[k] || 0;
                    const isUnlimited = total >= 999;
                    return (
                        <div
                            key={k}
                            className="rounded-sm border border-white/10 bg-card/50 p-3"
                            data-testid={`vip-ledger-${k}`}
                        >
                            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                {PERK_LABELS[k] || k}
                            </div>
                            <div className="font-serif text-2xl mt-1">
                                {isUnlimited ? (
                                    <span className="gold-text">∞</span>
                                ) : (
                                    <>
                                        <span className="gold-text">{rem}</span>
                                        <span className="text-sm text-muted-foreground"> / {total}</span>
                                    </>
                                )}
                            </div>
                            {!isUnlimited && used > 0 && (
                                <div className="text-[10px] text-muted-foreground mt-1">{used} used</div>
                            )}
                        </div>
                    );
                })}
            </div>

            <Link
                to="/vip"
                className="mt-6 inline-block text-xs uppercase tracking-[0.2em] text-muted-foreground hover:gold-text border-b border-white/10 pb-1"
                data-testid="vip-manage-link"
            >
                Manage membership →
            </Link>
        </div>
    );
}
